import React, {useCallback, useState} from 'react';
import {useForm} from 'react-hook-form';
import {toast} from 'react-toastify';
import {t} from '@lingui/core/macro';
import {Trans} from '@lingui/react';

import {TAG_LABEL_MAX} from 'shared/lib/constants';
import useRemoveTagFilter from 'shared/lib/hooks/useRemoveTagFilter';
import {validateTagLabel} from 'shared/lib/validation/validateTagLabel';
import ITag from 'shared/model/ITags';
import Button from 'shared/ui/Button/Button/Button';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import {DeleteButton} from 'shared/ui/IconButton/iconButtons';
import InputText from 'shared/ui/Input/InputText';
import ConfirmModal from 'shared/ui/Modal/ConfirmModal/ConfirmModal';
import MainModal from 'shared/ui/Modal/MainModal';
import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import SpinnerWithOverlay from 'shared/ui/SpinnerWithOverlay/SpinnerWithOverlay';

import {tagsAPI} from '../../api/tagsAPI';
import {getAddTagError} from '../../lib/addTagError';
import {sortTags} from '../../lib/tagsSort';
import useTagsSort from '../../model/useTagsSort';

import TagsSortToggle from './TagsSortToggle/TagsSortToggle';

import styles from './TagsManageModal.module.scss';

interface ITagsManageModalProps {
    open: boolean,
    handleClose: () => void,
}

interface ITagCreateForm {
    label: string,
}

const TagsManageModal: React.FC<ITagsManageModalProps> = ({open, handleClose}) => {
    const {data: tags = [], isLoading} = tagsAPI.useGetTagsQuery(undefined, {skip: !open});
    const [addTag, {isLoading: isAdding}] = tagsAPI.useAddTagMutation();
    const [deleteTag, {isLoading: isDeleting}] = tagsAPI.useDeleteTagMutation();
    const {mode, changeMode} = useTagsSort();
    const removeTagFilter = useRemoveTagFilter();
    const [tagToDelete, setTagToDelete] = useState<ITag | null>(null);

    const {control, register, handleSubmit, reset, setError, formState: {errors}} = useForm<ITagCreateForm>({
        defaultValues: {label: ''},
    });

    const onSubmit = async({label}: ITagCreateForm) => {
        try {
            await addTag(label).unwrap();
            reset();
        } catch (error) {
            setError('label', {type: 'server', message: getAddTagError(error)});
        }
    };

    const closeModal = useCallback(() => {
        reset();
        handleClose();
    }, [reset, handleClose]);

    const closeConfirm = useCallback(() => setTagToDelete(null), []);

    const onConfirmDelete = useCallback(async() => {
        if (!tagToDelete) return;

        const result = await deleteTag(tagToDelete.id);
        if ('error' in result) return;

        removeTagFilter(tagToDelete.label);
        toast.info(t`Тег удалён`);
        setTagToDelete(null);
    }, [deleteTag, removeTagFilter, tagToDelete]);

    const sortedTags = sortTags(tags, mode);

    return (
        <>
            <MainModal header={t`Теги`} open={open && !tagToDelete} handleClose={closeModal} size="sm">
                <ModalBodyWrapper>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <FormGroupWithError label={t`Новый тег`} error={errors.label?.message}>
                            <div className={styles.create}>
                                <div className={styles.createField}>
                                    <InputText
                                        name="label"
                                        control={control}
                                        register={register}
                                        placeholder={t`Название тега`}
                                        maxLength={TAG_LABEL_MAX}
                                        validate={false}
                                        rules={{validate: validateTagLabel}}
                                    />
                                </div>
                                <Button type="submit" loading={isAdding}>{t`Добавить`}</Button>
                            </div>
                        </FormGroupWithError>
                    </form>

                    <div className={styles.toolbar}>
                        <span className={styles.toolbarLabel}>{t`Сортировка`}</span>
                        <TagsSortToggle mode={mode} onChange={changeMode}/>
                    </div>

                    {isLoading && <SpinnerWithOverlay overlay={false}/>}

                    {!isLoading && sortedTags.length === 0 && (
                        <p className={styles.empty}>
                            <Trans
                                id="modal.tags.empty"
                                message="Тегов пока нет — создайте первый в поле выше"
                            />
                        </p>
                    )}

                    {sortedTags.length > 0 && (
                        <div className={styles.list}>
                            {sortedTags.map((tag) => (
                                <div key={tag.id} className={styles.row}>
                                    <span className={styles.label} title={tag.label}>{tag.label}</span>
                                    <DeleteButton
                                        iconSize={16}
                                        title={t`Удалить тег`}
                                        aria-label={t`Удалить тег`}
                                        onClick={() => setTagToDelete(tag)}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </ModalBodyWrapper>
            </MainModal>

            <ConfirmModal
                variant="delete"
                open={!!tagToDelete}
                handleClose={closeConfirm}
                header={t`Удаление тега`}
                title={t`Удалить тег`}
                name={tagToDelete?.label}
                hint={
                    <Trans
                        id="modal.tag.delete.text"
                        message="Тег снимется со всех мониторов и серверов, сами они не удалятся."
                    />
                }
                hintTone="warning"
                confirmText={t`Удалить`}
                onConfirm={onConfirmDelete}
                isLoading={isDeleting}
            />
        </>
    );
};

export default TagsManageModal;
